import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Formik } from 'formik';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import OTPField from '../../components/common/OTPField';
import RecaptchaV2Net from '../../components/RecaptchaV2Net';
import { resetAuthSetup } from '../../services/auth-setup/auth-setup-service';
import { getAPIKey, login, signInConfirm } from '../../services/login/login-service';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch, store } from '../../store';
import {
  changeLanguage,
  logout,
  saveUserData,
  setCurrencyDetails,
  userDetail as setUserDetail,
} from '../../store/actions/app.Actions';
import type { IWhatsAppOTPProps } from '../../types/propTypes';
import { Common } from '../../utils/constants';
import { useCountdownTimer } from '../../utils/useCountdownTimer';
import { AccountMigrationOTPSchema } from '../../validation/login/login-validation';
import UpdateSecurityModal from './UpdateSecurityModal';

const WhatsAppOTP = ({ email, password, onChangeStage, setIsResetAuthMethod, phoneDetail }: IWhatsAppOTPProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();
  const [useV2, setUseV2] = useState(false);

  const [siteKey, setSiteKey] = useState<string>('');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const [captchaSolved, setCaptchaSolved] = useState<boolean>(false);
  const [captchaError, setCaptchaError] = useState<boolean>(false);

  const [otpValidationTime, setOTPValidationTime] = useState<string>('');
  const [isShowUpdateSecurityModal, setIsShowUpdateSecurityModal] = useState<boolean>(false);
  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  const { secondsLeft, start, formattedTime, isRunning } = useCountdownTimer({
    initialSeconds: 45,
    autoStart: true,
  });

  useEffect(() => {
    if (useV2) {
      getRecaptchaSiteKey();
      setCaptchaSolved(false);
      setCaptchaError(true);
    }
  }, [useV2]);

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    )?.value;
    setSiteKey(siteKey);
  };

  useEffect(() => {
    getOTPValidationTime();
  }, []);

  const getOTPValidationTime = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.LOGIN_VERIFICATION_CODE_EXPIRY,
    });
    const otpValidationTime = config.data?.find(
      (item) => item.reference_code === Common.ConfigurationKey.LOGIN_VERIFICATION_CODE_EXPIRY,
    )?.value;
    setOTPValidationTime(otpValidationTime);
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleNavigationAfterMFASetup = async (response) => {
    try {
      const userDetail = await getProfile();

      const authSession = await fetchAuthSession();
      const authResponse = { ...response, authSession };

      if (userDetail?.Data?.status?.code === Common.UserStatus.CANCELLED) {
        dispatch(logout()).then(() => {
          toast.error(t('login.toast.cancelled'), { autoClose: 15000 });
        });
        return;
      }

      dispatch(saveUserData({ ...authResponse, ...userDetail.Data }));
      dispatch(changeLanguage(userDetail?.Data?.language_preference));
      dispatch(setCurrencyDetails(userDetail?.Data?.default_currency));
      dispatch(setUserDetail(userDetail?.Data));

      const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';

      if (downloadVATRedirect) {
        sessionStorage.removeItem('vat_receipt_path');
        navigate(downloadVATRedirect);
        return;
      }
      if (userDetail?.Data.is_profile_verified === false) {
        navigate(Common.Routes.PROFILE_REDIRECT);
        return;
      }

      if (userDetail?.Data.payment_type === null) {
        navigate(Common.Routes.PAYMENT);
        return;
      }
      navigate(Common.Routes.HOME);
    } catch (error) {
      toast.error(t('login.toast.error'));
      navigate(Common.Routes.HOME, { replace: true });
    }
    moment.locale(store.getState().UserData?.language?.code);
  };

  const handleConfirmWhatsAppOTP = async (code: string) => {
    setIsSubmittingOTP(true);
    try {
      document.getElementById('loading')?.classList.add('manual-loading');
      const response = await signInConfirm({ code });

      if (
        !response.isSignedIn &&
        response.nextStep.signInStep === Common.AuthenticationSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
      ) {
        toast.error(t('signup.toast.invalidCode'));
        return;
      }

      await handleNavigationAfterMFASetup(response);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.name === Common.Authentication.CodeMismatchException) {
        toast.error(t('signup.toast.invalidCode'));
      } else if (error.name === Common.Authentication.NotAuthorizedException) {
        onChangeStage(Common.AuthStage.SIGNIN);
      } else {
        toast.error(error.message.toString());
      }
    } finally {
      document.getElementById('loading')?.classList.remove('manual-loading');
      setIsSubmittingOTP(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await resetAuthSetup(email);

      if (!executeRecaptcha) return;

      try {
        document.getElementById('loading')?.classList.remove('spinner-disable');
        const token = await executeRecaptcha('login');
        await login(
          {
            email: email,
            password: password,
            recaptchaCode: token,
          },
          false,
          true,
        );
        document.getElementById('loading')?.classList.add('spinner-disable');

        onChangeStage(Common.AuthStage.SIGNIN_OTP);
        setIsResetAuthMethod(true);
      } catch {
        onChangeStage(Common.AuthStage.SIGNIN);
      }

      const params = {
        isUseDifferentMethod: true,
        email,
      };
      localStorage.setItem(Common.RESET_AUTH_METHOD, JSON.stringify(params));
    } catch (error) {
      toast.error(error.response?.data?.message || error.toString());
    }
  };

  const handleRecaptchaChange = async (recaptchaCode: string | null) => {
    if (recaptchaCode) {
      setCaptchaSolved(true);
      setCaptchaError(false);
      await resendOtpSubmit(recaptchaCode);
      recaptchaRef.current?.reset();
      setSiteKey('');
      setUseV2(false);
      start();
    }
  };

  const handleResendClick = async (
    resetForm: () => void,
    setFieldTouched: (field: string, touched: boolean) => void,
  ) => {
    resetForm();
    setFieldTouched('code', false);
    if (!executeRecaptcha) {
      return;
    }
    try {
      const token = await executeRecaptcha('resend_code');
      await resendOtpSubmit(token);
      start();
    } catch {
      setUseV2(true);
      if (useV2 && !captchaSolved) {
        setCaptchaError(true);
        return;
      }
    }
  };

  const resendOtpSubmit = async (recaptchaCode: string) => {
    document.getElementById('loading')?.classList.remove('spinner-disable');
    await login(
      {
        email: email,
        password: password,
        recaptchaCode,
      },
      useV2,
      false,
    )
      .then(() => {
        toast.success(t('authentication.authPhoneOTP.resendCodeToWhatsapp'));
        if (useV2) {
          setUseV2(false);
        }
      })
      .catch(() => {
        if (!useV2) {
          setUseV2(true);
          return;
        }
        toast.error(t('signup.toast.error'));
      })
      .finally(() => {
        document.getElementById('loading')?.classList.add('spinner-disable');
      });
  };

  const closeModal = () => {
    setIsShowUpdateSecurityModal(false);
  };

  return (
    <div className="auth-options-container">
      <div className="auth-header">
        <div className="auth-icon-container my-4">
          <img src={images.whatsAppIconSVG} alt="Security" className="auth-icon-image" />
        </div>
        <h1 className="auth-title mb-3">{t('authentication.authPhoneOTP.header')}</h1>
        <p className="auth-subtitle sign-in-otp-subtitle p-1">{t('authentication.authPhoneOTP.subHeader')}</p>
        <p className="phone-number-text">
          {phoneDetail.isdCode} {phoneDetail.phoneNumber}
        </p>

        <p className="signin-otp-validation-time mb-1">
          {t('signup.label.otpValidationTimeTitle').replace('##VALIDATIONTIME##', otpValidationTime)}
        </p>
      </div>

      <Formik
        initialValues={{ code: '' }}
        onSubmit={async (values) => {
          if (isSubmittingOTP) return;
          await handleConfirmWhatsAppOTP(values.code);
        }}
        validationSchema={AccountMigrationOTPSchema(t)}
      >
        {({ handleSubmit, resetForm, setFieldTouched }) => (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && isSubmittingOTP) {
                event.preventDefault();
              }
            }}
          >
            <div className="app-setup-otp-container p-1 mb-4">
              <OTPField name="code" customStyleClass="app-setup-otp" />
            </div>

            <div className="auth-app-button-OTP-wrapper">
              <button type="submit" className="btn btn-primary auth-app-button" disabled={isSubmittingOTP}>
                {t('authentication.button.continue')}
              </button>
              <p className="different-auth-method" onClick={() => setIsShowUpdateSecurityModal(true)}>
                {t('authentication.button.differentMethod')}
              </p>
            </div>
            {isRunning && secondsLeft > 0 ? (
              <div className="clearfix pt-2">
                <p className="sign-up-otp pb-0">{t('signup.label.otpSentMessage')}</p>
                <p className="sign-up-otp">
                  <span>
                    {t('signup.label.resendMessage')} {formattedTime}
                  </span>
                </p>
              </div>
            ) : (
              <div className="clearfix pt-1">
                <p className="sign-up-otp pb-0 mb-2">{t('authentication.authPhoneOTP.resendTitle')} </p>
                <p className="sign-up-otp">
                  <a
                    href="#"
                    title={t<string>('signup.button.resend')}
                    onClick={(event) => {
                      event.preventDefault();
                      handleResendClick(resetForm, setFieldTouched);
                    }}
                  >
                    {t('signup.button.resend')}
                  </a>
                </p>
              </div>
            )}
            {useV2 && siteKey && (
              <div className="mt-3 d-flex justify-content-center">
                <RecaptchaV2Net
                  ref={recaptchaRef}
                  sitekey={siteKey}
                  onChange={handleRecaptchaChange}
                  hl={
                    store?.getState()?.UserData?.language?.code
                      ? store?.getState()?.UserData?.language?.code
                      : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                  }
                  key={
                    store?.getState()?.UserData?.language?.code
                      ? store?.getState()?.UserData?.language?.code
                      : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                  }
                />
              </div>
            )}
            {captchaError && (
              <p className="text-center mt-2" style={{ color: '#d73a1f', fontSize: '12px' }}>
                {t('signup.toast.verifyRecaptchaToResend')}
              </p>
            )}
          </form>
        )}
      </Formik>

      {isShowUpdateSecurityModal && (
        <UpdateSecurityModal
          isShowUpdateSecurityModal={isShowUpdateSecurityModal}
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          showBackButton={true}
        />
      )}
    </div>
  );
};

export default WhatsAppOTP;
